import React, { useEffect, useState } from 'react';
import { Check, Zap, Shield, Headphones, Globe, ChevronRight } from 'lucide-react';
import api from '../api';

const PERKS = {
  free:     ['1 active automation', 'Basic integrations', 'Community support', 'Google Suite'],
  starter:  ['3 active automations', 'All integrations', 'Email support', 'Priority build queue', 'Google Suite'],
  growth:   ['10 active automations', 'All integrations', 'Priority support', 'Advanced AI models', 'Custom webhooks', 'Analytics dashboard'],
  business: ['Unlimited automations', 'All integrations', 'Dedicated support', 'Advanced AI models', 'Custom webhooks', 'Analytics dashboard', 'White-label option', 'API access'],
};

const TIER_COLORS = {
  free: { accent: 'var(--text3)', bg: 'transparent' },
  starter: { accent: 'var(--green)', bg: 'rgba(34,197,94,0.05)' },
  growth: { accent: 'var(--accent)', bg: 'rgba(99,102,241,0.05)' },
  business: { accent: 'var(--yellow)', bg: 'rgba(245,158,11,0.05)' },
};

export default function Billing() {
  const [tiers, setTiers] = useState([]);
  const [user, setUser] = useState(null);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    api.get('/tiers').then(r => setTiers(r.data)).catch(() => {});
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
    api.get('/auth/me').then(r => { setUser(r.data); localStorage.setItem('user', JSON.stringify(r.data)); }).catch(() => {});
  }, []);

  const price = (monthly) => annual ? Math.round(monthly * 0.8) : monthly;

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 40, marginBottom: 6 }}>PLAN & BILLING</h1>
          <p style={{ color: 'var(--text2)', fontSize: 15 }}>
            You're on the <span style={{ color: TIER_COLORS[user?.tier]?.accent || 'var(--text)', fontWeight: 800, textTransform: 'uppercase' }}>{user?.tier || 'free'}</span> plan.
          </p>
        </div>
        {/* Annual/Monthly toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 14px' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: !annual ? 'var(--text)' : 'var(--text3)' }}>Monthly</span>
          <div onClick={() => setAnnual(!annual)} style={{ width: 44, height: 24, background: annual ? 'var(--accent)' : 'var(--bg4)', borderRadius: 12, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', border: '1px solid var(--border2)' }}>
            <div style={{ position: 'absolute', width: 18, height: 18, borderRadius: '50%', background: 'white', top: 2, left: annual ? 22 : 2, transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: annual ? 'var(--text)' : 'var(--text3)' }}>Annual</span>
          {annual && <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--green)', background: 'var(--green-bg)', padding: '2px 8px', borderRadius: 20, border: '1px solid var(--green-border)' }}>SAVE 20%</span>}
        </div>
      </div>

      {/* Pricing cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        {tiers.map(t => {
          const isCurrent = user?.tier === t.tier;
          const isPopular = t.tier === 'growth';
          const tc = TIER_COLORS[t.tier] || TIER_COLORS.free;
          return (
            <div key={t.tier} style={{
              background: isCurrent ? tc.bg : 'var(--bg2)',
              border: `1.5px solid ${isCurrent ? tc.accent : isPopular ? 'rgba(99,102,241,0.3)' : 'var(--border)'}`,
              borderRadius: 'var(--radius)', padding: '22px 20px',
              position: 'relative', transition: 'all 0.2s',
              boxShadow: isCurrent ? `0 0 32px ${tc.accent}22` : 'none'
            }}
            onMouseEnter={e => { if (!isCurrent) { e.currentTarget.style.borderColor = tc.accent; e.currentTarget.style.transform = 'translateY(-3px)'; }}}
            onMouseLeave={e => { if (!isCurrent) { e.currentTarget.style.borderColor = isPopular ? 'rgba(99,102,241,0.3)' : 'var(--border)'; e.currentTarget.style.transform = 'none'; }}}>

              {(isCurrent || isPopular) && (
                <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: isCurrent ? tc.accent : 'linear-gradient(135deg, var(--accent), var(--purple))', color: 'white', fontSize: 10, fontWeight: 800, padding: '4px 12px', borderRadius: 20, whiteSpace: 'nowrap', letterSpacing: '0.06em' }}>
                  {isCurrent ? '✓ CURRENT PLAN' : '⚡ MOST POPULAR'}
                </div>
              )}

              <div style={{ fontSize: 11, fontWeight: 800, color: tc.accent, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>{t.label}</div>

              <div style={{ marginBottom: 18 }}>
                {t.price_monthly === 0
                  ? <div style={{ fontFamily: 'Bebas Neue', fontSize: 42, color: 'var(--text)', lineHeight: 1 }}>FREE</div>
                  : <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
                      <div style={{ fontFamily: 'Bebas Neue', fontSize: 42, color: 'var(--text)', lineHeight: 1 }}>${price(t.price_monthly)}</div>
                      <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>/mo</div>
                    </div>
                }
                {annual && t.price_monthly > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600, marginTop: 3 }}>
                    ${t.price_monthly}/mo billed annually
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 20 }}>
                {(PERKS[t.tier] || []).map(perk => (
                  <div key={perk} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 9 }}>
                    <Check size={13} color={tc.accent} strokeWidth={3} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.4 }}>{perk}</span>
                  </div>
                ))}
              </div>

              <button
                className={`btn ${isCurrent ? 'btn-secondary' : 'btn-primary'}`}
                style={{ width: '100%', justifyContent: 'center', fontSize: 13, fontWeight: 700, ...(isCurrent ? {} : { background: `linear-gradient(135deg, ${tc.accent === 'var(--text3)' ? 'var(--bg4)' : tc.accent}, ${tc.accent === 'var(--text3)' ? 'var(--bg5)' : tc.accent})` }) }}
                disabled={isCurrent}
                onClick={() => !isCurrent && alert('Stripe checkout coming soon — launching this week!')}>
                {isCurrent ? 'Current Plan' : t.price_monthly === 0 ? 'Downgrade' : `Upgrade to ${t.label}`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Feature highlights row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { icon: <Zap size={18} />, title: 'AI-Powered', desc: 'Gemini & Claude build your automations instantly' },
          { icon: <Shield size={18} />, title: 'Secure OAuth', desc: 'Your credentials never leave your account' },
          { icon: <Globe size={18} />, title: '12+ Integrations', desc: 'Gmail, Sheets, Telegram, Slack and more' },
          { icon: <Headphones size={18} />, title: 'Real Support', desc: 'Get help from our team, not just docs' },
        ].map(f => (
          <div key={f.title} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '18px 20px' }}>
            <div style={{ width: 36, height: 36, background: 'var(--accent-glow)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, color: 'var(--accent3)' }}>
              {f.icon}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{f.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>{f.desc}</div>
          </div>
        ))}
      </div>

      {/* Custom plan */}
      <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(168,85,247,0.06))', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 'var(--radius)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 42, height: 42, background: 'var(--accent-glow)', borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(99,102,241,0.3)', flexShrink: 0 }}>
            <Zap size={20} color="var(--accent3)" fill="var(--accent3)" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>Need a custom enterprise plan?</div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>White-labelling, custom integrations, dedicated infrastructure, volume pricing</div>
          </div>
        </div>
        <button className="btn btn-secondary" style={{ flexShrink: 0 }} onClick={() => alert('Contact: support@astrautomations.com')}>
          Contact Sales <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
